import React, { useEffect, useRef, useCallback } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import Stats from './components/Stats';
import Services from './components/Services';
import Portfolio from './components/Portfolio';
import About from './components/About';
import Process from './components/Process';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';
import DebugInfo from './components/DebugInfo';

function App() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const magneticButtonsRef = useRef<Element[]>([]);
  const animationFrameRef = useRef<number | null>(null);

  // Optimized cursor movement with throttling
  const moveCursor = useCallback((e: MouseEvent) => {
    // Cancel previous animation frame to prevent lag
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(() => {
      const cursorDot = document.querySelector('.cursor-dot') as HTMLElement;
      const cursorOutline = document.querySelector('.cursor-outline') as HTMLElement;
      
      if (cursorDot && cursorOutline) {
        cursorDot.style.left = e.clientX + 'px';
        cursorDot.style.top = e.clientY + 'px';
        cursorOutline.style.left = e.clientX + 'px';
        cursorOutline.style.top = e.clientY + 'px';
      }
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    const cursorDot = document.querySelector('.cursor-dot') as HTMLElement;
    const cursorOutline = document.querySelector('.cursor-outline') as HTMLElement;
    
    if (cursorDot && cursorOutline) {
      cursorDot.style.transform = 'scale(1.5)';
      cursorOutline.style.transform = 'scale(1.5)';
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    const cursorDot = document.querySelector('.cursor-dot') as HTMLElement;
    const cursorOutline = document.querySelector('.cursor-outline') as HTMLElement;
    
    if (cursorDot && cursorOutline) {
      cursorDot.style.transform = 'scale(1)';
      cursorOutline.style.transform = 'scale(1)';
    }
  }, []);


  useEffect(() => {
    // Check if device is mobile/touch device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                     ('ontouchstart' in window) || 
                     (navigator.maxTouchPoints > 0);
    
    // Only add cursor effects on non-mobile devices
    if (isMobile) {
      // Hide cursor elements on mobile
      const cursorDot = document.querySelector('.cursor-dot') as HTMLElement;
      const cursorOutline = document.querySelector('.cursor-outline') as HTMLElement;
      if (cursorDot) cursorDot.style.display = 'none';
      if (cursorOutline) cursorOutline.style.display = 'none';
      return;
    }

    // Add scroll-triggered animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Observe all elements with fade-in class
    const fadeElements = document.querySelectorAll('.fade-in, .slide-up');
    fadeElements.forEach((el) => observerRef.current?.observe(el));

    // Add cursor effects to interactive elements
    const interactiveElements = document.querySelectorAll('button, a, input, textarea, select');
    
    interactiveElements.forEach((el) => {
      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('mouseleave', handleMouseLeave);
    });

    document.addEventListener('mousemove', moveCursor);


    // Magnetic button effect with throttling
    const magneticButtons = document.querySelectorAll('.magnetic');
    magneticButtonsRef.current = Array.from(magneticButtons);
    
    magneticButtonsRef.current.forEach((button) => {
      const buttonElement = button as HTMLElement;
      let magneticAnimationId: number;
      
      const handleMagneticMove = (e: Event) => {
        // Cancel previous animation frame
        if (magneticAnimationId) {
          cancelAnimationFrame(magneticAnimationId);
        }
        
        magneticAnimationId = requestAnimationFrame(() => {
          const mouseEvent = e as MouseEvent;
          const rect = buttonElement.getBoundingClientRect();
          const x = mouseEvent.clientX - rect.left - rect.width / 2;
          const y = mouseEvent.clientY - rect.top - rect.height / 2;
          buttonElement.style.transform = `translate(${x * 0.1}px, ${y * 0.1}px)`;
        });
      };

      const handleMagneticLeave = () => {
        if (magneticAnimationId) {
          cancelAnimationFrame(magneticAnimationId);
        }
        buttonElement.style.transform = 'translate(0px, 0px)';
      };

      button.addEventListener('mousemove', handleMagneticMove);
      button.addEventListener('mouseleave', handleMagneticLeave);
    });

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', moveCursor);
      
      // Cancel any pending animation frames
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // Remove cursor event listeners
      const interactiveElements = document.querySelectorAll('button, a, input, textarea, select');
      interactiveElements.forEach((el) => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('mouseleave', handleMouseLeave);
      });
      
      magneticButtonsRef.current.forEach((button) => {
        // Note: We can't easily remove these listeners without storing references
        // This is a limitation of the current implementation
      });
      
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [moveCursor, handleMouseEnter, handleMouseLeave]);

  return (
    <ThemeProvider>
      <div className="App">
        <Navigation />
        <main>
          <Hero />
          <Stats />
          <Services />
          <Portfolio />
          <About />
          <Process />
          <Testimonials />
          <Contact />
        </main>
        <Footer />
        <DebugInfo />
      </div>
    </ThemeProvider>
  );
}

export default App;
